import api from './api';
import type { OrgListItem, OrgSource } from '../types';

/** What deleting an org destroys + what blocks it — for the delete confirm (P1). */
export interface OrgDeletionImpact {
  isLastOrg: boolean;
  blockingSources: Array<{ id: string; name: string; isActive: boolean }>;
  homedUsers: number;
  wikiPages: number;
  semanticEntries: number;
  pulseJobs: number;
  canDelete: boolean;
}

/**
 * Workspace-admin management of the orgs (grouping labels) under the active
 * workspace (Workspaces Layer 3). All routes gate on MANAGE_ORG_SETTINGS and are
 * workspace-scoped in the backend.
 */
export const orgApi = {
  listOrganizations: async (): Promise<OrgListItem[]> => {
    const { data } = await api.get('/api/org/organizations');
    return data.organizations as OrgListItem[];
  },

  createOrganization: async (name: string): Promise<OrgListItem> => {
    const { data } = await api.post('/api/org/organizations', { name });
    return data.organization as OrgListItem;
  },

  updateOrganization: async (
    id: string,
    updates: { name?: string; defaultTimezone?: string },
  ): Promise<void> => {
    await api.patch(`/api/org/organizations/${id}`, updates);
  },

  deleteOrganization: async (id: string): Promise<void> => {
    await api.delete(`/api/org/organizations/${id}`);
  },

  /** Counts shown in the delete confirm (wiki/semantics/pulse) + what blocks the delete. Admin-only. */
  getDeletionImpact: async (id: string): Promise<OrgDeletionImpact> => {
    const { data } = await api.get(`/api/org/organizations/${id}/deletion-impact`);
    return data.impact as OrgDeletionImpact;
  },

  /** Sources (active + inactive) in an org — the move-sources picker. */
  listOrgSources: async (id: string): Promise<OrgSource[]> => {
    const { data } = await api.get(`/api/org/organizations/${id}/sources`);
    return data.sources as OrgSource[];
  },

  moveSources: async (sourceIds: string[], targetOrganizationId: string): Promise<number> => {
    const { data } = await api.post('/api/org/organizations/move-sources', {
      sourceIds,
      targetOrganizationId,
    });
    return data.moved as number;
  },
};
